import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  Container,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { regions, authCodes } from '../services/api';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const ProductVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const [regionCode, setRegionCode] = useState('');
  const [authenticityCode, setAuthenticityCode] = useState('');
  const [region, setRegion] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [qrCodeScanned, setQrCodeScanned] = useState(false);
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [subscribe, setSubscribe] = useState(false);

  // Auto-dismiss success/error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    const regionParam = searchParams.get('region');
    console.log('Region param:', regionParam);
    console.log('User agent:', navigator.userAgent);
    
    if (regionParam) {
      setRegionCode(regionParam);
      
      // Check if this is a mobile device (QR code scanned with mobile camera)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log('Is mobile:', isMobile);
      
      // Also check if the page was loaded with a region parameter (likely from QR scan)
      const hasRegionParam = window.location.search.includes('region=');
      console.log('Has region param:', hasRegionParam);
      
      if (isMobile || hasRegionParam) {
        // On mobile or if region param exists, assume QR was scanned
        console.log('Auto-verifying region for mobile or QR scan');
        setQrCodeScanned(true);
        setError(''); // Clear any previous errors
        
        // Auto-verify the region with a small delay to ensure state is set
        setTimeout(() => {
          console.log('Calling verifyRegion with:', regionParam);
          verifyRegion(regionParam);
        }, 100);
      } else {
        // On desktop, require QR scanner
        console.log('Desktop detected - requiring QR scan');
        setError('Please scan the QR code on your product to verify the region');
      }
    }
  }, [searchParams]);

  const verifyRegion = async (code) => {
    console.log('verifyRegion called with:', code);
    try {
      setLoading(true);
      console.log('Calling regions.getByCode with:', code);
      const response = await regions.getByCode(code);
      console.log('Region response:', response);
      setRegion(response.data);
      setSuccess(`Region verified: ${response.data.name}`);
      setQrCodeScanned(true);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Region verification error:', err);
      setError('Invalid region code');
    } finally {
      setLoading(false);
    }
  };

  const handleQRCodeScan = async (qrData) => {
    // This will be called from the CustomerLayout QR scanner
    try {
      setLoading(true);
      setError('');
      
      // Validate QR code with server
      const response = await regions.validateQR(qrData);
      
      if (response.data.valid) {
        // QR code is valid, now verify the region
        await verifyRegion(response.data.region);
      } else {
        setError('Invalid QR code. Please scan the QR code from your product.');
      }
    } catch (error) {
      console.error('QR validation error:', error);
      setError('Failed to validate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Expose the QR scan handler to parent component
  useEffect(() => {
    window.handleQRCodeScan = handleQRCodeScan;
    return () => {
      delete window.handleQRCodeScan;
    };
  }, [regionCode]);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          });
        },
        (error) => {
          console.warn('Location access denied or failed:', error);
          // Return null if location access is denied
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  const handleVerifyProduct = async (e) => {
    e.preventDefault();
    
    // if (!qrCodeScanned) {
    //   setError('Please scan the QR code first to verify the region');
    //   return;
    // }
    
    if (!authenticityCode) {
      setError('Please enter the authenticity code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Get current location
      let locationData = null;
      try {
        locationData = await getCurrentLocation();
        console.log('Location data:', locationData);
      } catch (error) {
        console.warn('Could not get location:', error);
      }
      
      // Use the new dedicated verification API
      const response = await authCodes.verifyProductCode({
        authenticityCode,
        location: locationData,
        userAgent: navigator.userAgent,
        ipAddress: null // Will be captured on server side
      });
      
      console.log('Verification API Response:', response);
      console.log('Response data:', response.data);
      console.log('Is valid:', response.data.isValid);
      console.log('Message:', response.data.message);
      
      setVerificationResult({
        isValid: response.data.isValid,
        message: response.data.message,
        productCode: response.data.productCode,
        regionName: response.data.regionName,
        verificationTime: response.data.verificationTime,
        location: response.data.location,
        // Show previous verification time if already verified
        previousVerificationTime: response.data.verificationTime && !response.data.isValid ? response.data.verificationTime : null,
        previousLocation: response.data.location && !response.data.isValid ? response.data.location : null
      });

      // Clear the authenticity code field if verification is successful
      if (response.data.isValid) {
        setAuthenticityCode('');
      }
      
    } catch (err) {
      console.error('Verification failed:', err);
      setError('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Grid container spacing={4}>
        {/* Left Section - How to Authenticate */}
        {/* <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#000',
                mb: 1,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              How to Authenticate?
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#000',
                mb: 3,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Watch how to authenticate your product
            </Typography> */}

            {/* Video Thumbnail */}
            {/* <Card 
              sx={{ 
                flex: 1,
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 3,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                }
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: { xs: '250px', md: '300px' },
                  background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              > */}
                {/* Product Image Placeholder */}
                {/* <Box
                  sx={{
                    width: '80%',
                    height: '60%',
                    background: 'linear-gradient(45deg, #2596be 0%, #4fb3d9 100%)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    mb: 2,
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#000',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      px: 2
                    }}
                  >
                    Product Code Example
                  </Typography>
                </Box> */}
                
                {/* Play Button */}
                {/* <PlayCircleOutlineIcon 
                  sx={{ 
                    fontSize: 60, 
                    color: '#ff0000',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }} 
                />
              </Box>
              
              <CardContent sx={{ p: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  On the product seal, you will find a sticker with a unique code printed on it.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grid> */}

        {/* Right Section - Check Authenticity */}
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#000',
                mb: 1,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Check Authenticity
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#000',
                mb: 4,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Know if your product is authentic
            </Typography>

            {/* QR Scan Requirement */}
            {/* {!qrCodeScanned && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  QR Code Scan Required
                </Typography>
                <Typography variant="body2">
                  Please scan the QR code on your product first to verify the region.
                </Typography>
              </Alert>
            )} */}

            {/* Region Verification Status */}
            {region && qrCodeScanned && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Region Verified: {region.name}
                </Typography>
              </Alert>
            )}

            {/* Verification Form */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 4, 
                borderRadius: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box component="form" onSubmit={handleVerifyProduct} sx={{ flex: 1 }}>
                {/* Unique Code Field */}
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#000',
                      mb: 1
                    }}
                  >
                    Enter Unique code
        </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="e.g. #H876543"
                      value={authenticityCode}
                      onChange={(e) => setAuthenticityCode(e.target.value.toUpperCase())}
                      // disabled={loading || !qrCodeScanned}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '1.1rem',
                        }
                      }}
                    />
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        background: 'linear-gradient(45deg, #2596be 0%, #4fb3d9 100%)',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <VerifiedUserIcon sx={{ color: '#000', fontSize: 30 }} />
                    </Box>
                  </Box>
      </Box>

                {/* Mobile Field */}
                {/* <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#000',
                      mb: 1
                    }}
                  >
                    Mobile
                  </Typography>
          <TextField
            fullWidth
                    placeholder="Mobile Number"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                      }
                    }}
                  />
                </Box> */}

                {/* Email Field */}
                {/* <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#000',
                      mb: 1
                    }}
                  >
                    Email (optional)
                  </Typography>
                  <TextField
              fullWidth
                    placeholder="Email Id (Optional)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                      }
                    }}
                  />
                </Box> */}

                {/* Check Now Button */}
            <Button
                  type="submit"
                  variant="contained"
                  // disabled={loading || !qrCodeScanned}
                  sx={{
                    width: '100%',
                    height: 60,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    backgroundColor: '#2596be',
                    color: '#000',
                    borderRadius: 2,
                    mb: 2,
                    '&:hover': {
                      backgroundColor: '#1e7ba8',
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#666',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Check Now'}
            </Button>

                {/* Subscribe Checkbox */}
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={subscribe}
                      onChange={(e) => setSubscribe(e.target.checked)}
                      sx={{
                        color: '#2596be',
                        '&.Mui-checked': {
                          color: '#2596be',
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#000' }}>
                      Subscribe me to articles, deals and more!
                    </Typography>
                  }
                /> */}
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Verification Result Modal */}
      {verificationResult && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Paper
            sx={{
              p: 4,
              maxWidth: 500,
              width: '90%',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
              {verificationResult.isValid ? (
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mr: 2 }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mr: 2 }} />
              )}
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: verificationResult.isValid ? 'success.main' : 'error.main'
                }}
              >
                {verificationResult.isValid ? 'Verification Successful!' : 'Verification Failed'}
              </Typography>
        </Box>

            <Typography variant="h6" sx={{ mb: 3 }}>
              {verificationResult.message}
            </Typography>
            
            {verificationResult.isValid && (
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Product Code:</strong> {verificationResult.productCode}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Region:</strong> {verificationResult.regionName}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Verified At:</strong> {verificationResult.verificationTime}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Location:</strong> {verificationResult.location}
                </Typography>
              </Box>
            )}
            
            <Button
              variant="contained"
              onClick={() => setVerificationResult(null)}
              sx={{
                backgroundColor: verificationResult.isValid ? '#4caf50' : '#f44336',
                color: 'white',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: verificationResult.isValid ? '#45a049' : '#da190b',
                }
              }}
            >
              {verificationResult.isValid ? 'Continue' : 'Try Again'}
            </Button>
          </Paper>
        </Box>
      )}

      {/* Error Messages */}
        {error && (
        <Alert 
          severity="error" 
          onClick={() => setError('')}
          sx={{ 
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 10000,
            minWidth: 300,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            }
          }}
        >
            {error}
          </Alert>
        )}

      {success && (
          <Alert
          severity="success" 
          onClick={() => setSuccess('')}
          sx={{ 
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 10000,
            minWidth: 300,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            }
          }}
        >
          {success}
          </Alert>
        )}
    </Container>
  );
};

export default ProductVerificationPage;